/*
TRANE CONFIDENTIAL

2021 – 2022 Copyright Trane U.S. Inc.
All Rights Reserved.

NOTICE: All information contained herein is, and remains the property of
Trane U.S. Inc. The source code, object code, intellectual and technical
concepts contained herein are proprietary to Trane U.S. Inc. and may be
covered by patents and patent applications and are protected by trade secret
and copyright law. Dissemination of this information or reproduction of this
material is strictly forbidden unless prior written permission is obtained
from Trane U.S. Inc. Do not use this software without the prior written
permission of Trane U.S. Inc.
*/

/* @flow */
import Backbone from "backbone";
import enqueueIn from "nx-components/utils/enqueueIn";
import { type Schema as APISchemaType } from "nexia_mobile/api/types";
import batchReactUpdates from "nexia_mobile/batchReactUpdates";
import TrackedScrollableArea from "nexia_mobile/components/Scrollable/TrackedScrollableArea";
import type Device from "nexia_mobile/models/device";
import type Automation from "nexia_mobile/models/automation";
import type Schema from "nexia_mobile/models/schema";
import type RecordedVideo from "nexia_mobile/models/recorded_video";
import type VideoFeed from "nexia_mobile/models/mixins/video_feed";
import type Enrollable from "nexia_mobile/models/enrollable";
import type Phone from "nexia_mobile/models/phone";
import type {
  Action,
  MemoryHistory,
  MemoryLocation,
} from "nexia_mobile/ReactRouterTypes";
import type { Link, ServerResponse, Url } from "nexia_mobile/api/types";
import type { XR724WizardVideos } from "nexia_mobile/components/Wizard/XR724Shared/wizardVideos";
import type { XR724EnrollmentWizardState } from "nexia_mobile/components/Wizard/XR724EnrollmentWizard";
import type { XR724WifiConfigurationWizardState } from "nexia_mobile/components/Wizard/XR724WifiConfigurationWizard";
import type { DragonflyEnrollmentWizardState } from "nexia_mobile/components/Wizard/DragonflyEnrollmentWizard";
import type { DragonflyWifiConfigurationWizardState } from "nexia_mobile/components/Wizard/DragonflyWifiConfigurationWizard";

type XR724EnrollmentWizardLocationState = {
  defaultWizardState: XR724EnrollmentWizardState,
  wizardVideos: XR724WizardVideos,
  onWizardFinish: () => ?Promise<mixed>,
  onWizardLeave: () => void,
  fetchEnrollmentDetails: () => Promise<{
    pin: string,
    statusUrl: Url,
    cancelLink?: ?Link<{||}, ServerResponse<null>>,
    childSchemaLink?: ?Link<{||}, ServerResponse<APISchemaType>>,
  }>,
};

type XR724WifiConfigurationWizardLocationState = {
  fetchDeviceLastUpdateTime: () => Promise<Date>,
  wizardVideos: XR724WizardVideos,
  onWizardFinish: () => ?Promise<mixed>,
  onWizardLeave: () => void,
  defaultWizardState: XR724WifiConfigurationWizardState,
};

type DragonflyEnrollmentWizardLocationState = {
  defaultWizardState: DragonflyEnrollmentWizardState,
  onWizardFinish: () => ?Promise<mixed>,
  fetchEnrollmentDetails: () => Promise<{
    pin: string,
    statusUrl: Url,
    cancelLink?: Link<{||}, ServerResponse<null>>,
  }>,
};

type DragonflyWifiConfigurationWizardLocationState = {
  device: Device,
  defaultWizardState: DragonflyWifiConfigurationWizardState,
};

type RouteToLocationStateMappings = {
  "/": void,
  "/welcome": void,
  "/pin-entry/create": ?{ isPinReset: boolean },
  "/pin-entry/validate": void,
  "/reauth": {
    authenticationParams?: {},
    prompt: string,
    submitButtonText?: string,
    title: string,
    onUserAuthenticated: () => mixed,
  },
  "/signin": void,
  "/signin/activate-phone": void,
  "/signin/forgot-credentials": void,
  "/signup": void,
  "/signup/user-info": void,
  "/signup/challenge-terms": { challengeSchema: Schema },
  "/signup/account-created": {
    mobileId: number,
    apiKey: string,
    setupStep: string,
  },
  "/signup/survey": { mobileId: number, apiKey: string },
  "/signup/promo-code": { mobileId: number, apiKey: string },
  "/transfer-account": void,
  "/transfer-account/user-info": void,
  "/transfer-account/challenge-terms": { challengeSchema: Schema },
  "/app": void,
  "/app/home": void,
  "/app/devices/details": { device: Device },
  "/app/devices/schedule": { device: Device, schedule: Backbone.Model },
  "/app/devices/runtime-history": { device: Device },
  "/app/devices/settings": { device: Device },
  "/app/devices/occupancy-setpoints": { device: Device },
  "/app/devices/user-codes": { device: Device },
  "/app/devices/user-codes/edit": { device: Device, schema: Schema },
  "/app/devices/user-codes/progress": { device: Device, schema: Schema },
  "/app/devices/temporary-code-edit": { device: Device },
  "/app/devices/add": void,
  "/app/devices/add/enroll-device": { enrollableSchema: Schema },
  "/app/devices/add/enrolling-device": { enrollable: Enrollable },
  "/app/devices/advanced": { device: Device },
  "/app/devices/thermostat-alerts": { device: Device },
  "/app/devices/thermostat-alerts/fault": { device: Device },
  "/app/devices/thermostat-alerts/highlowfault": { device: Device },
  "/app/devices/thermostat-alerts/humidityfault": { device: Device },
  "/app/devices/thermostat-alerts/othertemperature": {
    device: Device,
    giValue: Device,
  },
  "/app/devices/thermostat-alerts/binaryinputalert": {
    device: Device,
    giValue: Device,
  },
  "/app/devices/pin-access": { device: Device },
  "/app/devices/access-restriction": { device: Device },
  "/app/alerts": void,
  "/app/alerts/users": void,
  "/app/alerts/users/edit": { schema: Schema },
  "/app/automations": void,
  "/app/automations/edit": { editSchema: Schema },
  "/app/automations/details": { automation: Automation },
  "/app/events": ?{ filterHref: Url },
  "/app/videos": ?{ filterHref: Url },
  "/app/videos/edit": {
    editSchema: Schema,
    onCommitted: () => void,
    onDeleted: () => void,
  },
  "/app/settings": void,
  "/app/settings/home-settings": void,
  "/app/support": void,
  "/app/open-source-license-info": void,
  "/app/groups/add": void,
  "/app/groups/edit": { editSchema: Schema },
  "/app/video-feed": { model: RecordedVideo | (Device & VideoFeed) },
  "/app/multi-video-feed": { group: Device },
  "/app/xr724/enrollment-wizard": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/errors/ErrorClientUnableToConnectToThermostat": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/00_prepare_enrollment/StepEnrollmentOverview": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/00_prepare_enrollment/SpinnerFetchEnrollmentDetailsFromNexia": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/errors/ErrorNexiaFailedToProvideEnrollmentDetails": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/01_putting_thermostat_in_join_mode/StepPressMenuButtonOn724": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/01_putting_thermostat_in_join_mode/StepSelectWifiFrom724Menu": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/01_putting_thermostat_in_join_mode/StepSelectNetworkFrom724Menu": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/01_putting_thermostat_in_join_mode/StepStartAdHocModeOn724": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/01_putting_thermostat_in_join_mode/StepWaitFor724AdHocModeToStart": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/01_putting_thermostat_in_join_mode/StepSuccessfullyStartedAdHocModeOn724": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/02_connect_device_to_thermostat/StepConnectTo724AdHoc": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/02_connect_device_to_thermostat/StepLookForUserKey": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/02_connect_device_to_thermostat/StepEnterUserKey": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/02_connect_device_to_thermostat/SpinnerVerifyUserKey": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/errors/ErrorInvalidUserKey": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/02_connect_device_to_thermostat/SpinnerSendConnectionPinTo724": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/errors/ErrorCouldNotConnectTo724AdHoc": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/02_connect_device_to_thermostat/StepSuccessfullyConnectedTo724AdHoc": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/03_choose_wifi_network_for_thermostat/Spinner724ScansForWifiNetworks": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/errors/Error724FailedToScanForNetworks": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/03_choose_wifi_network_for_thermostat/StepChooseWifiNetworkFor724ToConnectTo": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/03_choose_wifi_network_for_thermostat/StepWriteInNetworkFor724ToConnectTo": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/04_thermostat_connects_to_nexia/Spinner724ConnectsToWifiNetworkAndNexia": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/errors/ErrorServerSaidThatEnrollmentFailed": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/04_thermostat_connects_to_nexia/Step724SuccessfullyConnected": XR724EnrollmentWizardLocationState,
  "/app/xr724/enrollment-wizard/05_recovery/StepReturnTo724HomeScreen": XR724EnrollmentWizardLocationState,
  "/app/xr724/wifi-configuration-wizard": XR724WifiConfigurationWizardLocationState,
  "/app/xr724/wifi-configuration-wizard/errors/ErrorClientUnableToConnectToThermostat": XR724WifiConfigurationWizardLocationState,
  "/app/xr724/wifi-configuration-wizard/00_overview/StepConfigurationOverview": XR724WifiConfigurationWizardLocationState,
  "/app/xr724/wifi-configuration-wizard/01_disconnect_thermostat_from_current_network/StepPressMenuButtonOn724": XR724WifiConfigurationWizardLocationState,
  "/app/xr724/wifi-configuration-wizard/01_disconnect_thermostat_from_current_network/StepSelectWifiFrom724Menu": XR724WifiConfigurationWizardLocationState,
  "/app/xr724/wifi-configuration-wizard/01_disconnect_thermostat_from_current_network/StepSelectNetworkFrom724Menu": XR724WifiConfigurationWizardLocationState,
  "/app/xr724/wifi-configuration-wizard/01_disconnect_thermostat_from_current_network/StepLeaveNetwork": XR724WifiConfigurationWizardLocationState,
  "/app/xr724/wifi-configuration-wizard/01_disconnect_thermostat_from_current_network/StepSuccessfullyLeftNetwork": XR724WifiConfigurationWizardLocationState,
  "/app/xr724/wifi-configuration-wizard/02_putting_thermostat_in_join_mode/StepSelectNetworkFrom724MenuForJoin": XR724WifiConfigurationWizardLocationState,
  "/app/xr724/wifi-configuration-wizard/02_putting_thermostat_in_join_mode/StepStartAdHocModeOn724": XR724WifiConfigurationWizardLocationState,
  "/app/xr724/wifi-configuration-wizard/02_putting_thermostat_in_join_mode/StepWaitFor724AdHocModeToStart": XR724WifiConfigurationWizardLocationState,
  "/app/xr724/wifi-configuration-wizard/02_putting_thermostat_in_join_mode/StepSuccessfullyStartedAdHocModeOn724": XR724WifiConfigurationWizardLocationState,
  "/app/xr724/wifi-configuration-wizard/03_connect_device_to_thermostat/StepConnectTo724AdHoc": XR724WifiConfigurationWizardLocationState,
  "/app/xr724/wifi-configuration-wizard/03_connect_device_to_thermostat/StepLookForUserKey": XR724WifiConfigurationWizardLocationState,
  "/app/xr724/wifi-configuration-wizard/03_connect_device_to_thermostat/StepEnterUserKey": XR724WifiConfigurationWizardLocationState,
  "/app/xr724/wifi-configuration-wizard/03_connect_device_to_thermostat/Spinner724ScansForWifiNetworks": XR724WifiConfigurationWizardLocationState,
  "/app/xr724/wifi-configuration-wizard/errors/ErrorCouldNotConnectTo724AdHoc": XR724WifiConfigurationWizardLocationState,
  "/app/xr724/wifi-configuration-wizard/03_connect_device_to_thermostat/StepSuccessfullyConnectedTo724AdHoc": XR724WifiConfigurationWizardLocationState,
  "/app/xr724/wifi-configuration-wizard/04_choose_wifi_network_for_thermostat/StepChooseWifiNetworkFor724ToConnectTo": XR724WifiConfigurationWizardLocationState,
  "/app/xr724/wifi-configuration-wizard/04_choose_wifi_network_for_thermostat/StepWriteInNetworkFor724ToConnectTo": XR724WifiConfigurationWizardLocationState,
  "/app/xr724/wifi-configuration-wizard/05_thermostat_connects_to_nexia/Spinner724ConnectsToNewWifiNetwork": XR724WifiConfigurationWizardLocationState,
  "/app/xr724/wifi-configuration-wizard/errors/ErrorThermostatDidNotReconnect": XR724WifiConfigurationWizardLocationState,
  "/app/xr724/wifi-configuration-wizard/05_thermostat_connects_to_nexia/Step724SuccessfullyReconnectedWithNewWifiNetwork": XR724WifiConfigurationWizardLocationState,
  "/app/dragonfly/enrollment-wizard": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/enrollment-wizard/errors/ErrorClientUnableToConnectToDragonfly": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/enrollment-wizard/00_prepare_enrollment/StepEnrollmentOverview": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/enrollment-wizard/00_prepare_enrollment/SpinnerFetchEnrollmentDetailsFromNexia": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/enrollment-wizard/errors/ErrorNexiaFailedToProvideEnrollmentDetails": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/enrollment-wizard/01_putting_dragonfly_in_softap_mode/StepPressButtonsOnRemote": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/enrollment-wizard/01_putting_dragonfly_in_softap_mode/StepSuccessfullyStartedSoftAPModeOnDragonfly": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/enrollment-wizard/02_connect_device_to_dragonfly/StepConnectDeviceToDragonflyAP": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/enrollment-wizard/02_connect_device_to_dragonfly/SpinnerSendConnectionPinToDragonfly": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/enrollment-wizard/errors/ErrorCouldNotConnectToDragonflyAP": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/enrollment-wizard/02_connect_device_to_dragonfly/StepSuccessfullyConnectedToDragonflyAP": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/enrollment-wizard/03_choose_wifi_network_for_dragonfly/SpinnerDragonflyScansForWifiNetworks": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/enrollment-wizard/errors/ErrorDragonflyFailedToScanForNetworks": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/enrollment-wizard/03_choose_wifi_network_for_dragonfly/StepChooseWifiNetworkForDragonflyToConnectTo": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/enrollment-wizard/03_choose_wifi_network_for_dragonfly/StepWriteInNetworkForDragonflyToConnectTo": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/enrollment-wizard/04_dragonfly_connects_to_nexia/SpinnerDragonflyConnectsToWifiNetworkAndNexia": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/enrollment-wizard/errors/ErrorServerSaidThatEnrollmentFailed": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/enrollment-wizard/04_dragonfly_connects_to_nexia/StepDragonflySuccessfullyConnected": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/wifi-configuration-wizard": DragonflyWifiConfigurationWizardLocationState,
  "/app/dragonfly/wifi-configuration-wizard/errors/ErrorClientUnableToConnectToDragonfly": DragonflyWifiConfigurationWizardLocationState,
  "/app/dragonfly/wifi-configuration-wizard/00_overview/StepConfigurationOverview": XR724WifiConfigurationWizardLocationState,
  "/app/dragonfly/wifi-configuration-wizard/01_putting_dragonfly_in_softap_mode/StepPressButtonsOnRemote": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/wifi-configuration-wizard/01_putting_dragonfly_in_softap_mode/StepSuccessfullyStartedSoftAPModeOnDragonfly": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/wifi-configuration-wizard/02_connect_device_to_dragonfly/StepConnectDeviceToDragonflyAP": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/wifi-configuration-wizard/02_connect_device_to_dragonfly/SpinnerTestCanTalkToDragonfly": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/wifi-configuration-wizard/errors/ErrorCouldNotTalkToDragonfly": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/wifi-configuration-wizard/02_connect_device_to_dragonfly/StepSuccessfullyConnectedToDragonflyAP": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/wifi-configuration-wizard/03_choose_wifi_network_for_dragonfly/SpinnerDragonflyScansForWifiNetworks": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/wifi-configuration-wizard/errors/ErrorDragonflyFailedToScanForNetworks": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/wifi-configuration-wizard/03_choose_wifi_network_for_dragonfly/StepChooseWifiNetworkForDragonflyToConnectTo": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/wifi-configuration-wizard/03_choose_wifi_network_for_dragonfly/StepWriteInNetworkForDragonflyToConnectTo": DragonflyEnrollmentWizardLocationState,
  "/app/dragonfly/wifi-configuration-wizard/04_dragonfly_connects_to_nexia/SpinnerDragonflyConnectsToNewWifiNetwork": DragonflyWifiConfigurationWizardLocationState,
  "/app/dragonfly/wifi-configuration-wizard/errors/ErrorDragonflyDidNotReconnect": DragonflyWifiConfigurationWizardLocationState,
  "/app/dragonfly/wifi-configuration-wizard/04_dragonfly_connects_to_nexia/StepDragonflySuccessfullyReconnectedWithNewWifiNetwork": DragonflyWifiConfigurationWizardLocationState,
  "/app/phones": void,
  "/app/phones/details": { phone: Phone },
  "/app/phones/edit": { editSchema: Schema },
  "/app/buildings": void,
  "/app/buildings/details": { building: Location },
  "/app/buildings/edit": { editSchema: Schema },
};

type Route = $Keys<RouteToLocationStateMappings>;
export type LocationStateForRoute<RouteType> = $ElementType<
  RouteToLocationStateMappings,
  RouteType
>;

// TODO: eslint thinks this is unused, but it's being used in the object type spread comment in NexiaHistory.
// Once we upgrade babel to 7.x, we can remove this eslint-disable.
// eslint-disable-next-line no-unused-vars
type MemoryHistoryWithoutPushAndReplace = $Rest<
  MemoryHistory,
  {| push: any, replace: any |}
>;

export type NexiaHistory = {|
  // TODO: Once we're on babel 7.x then we'll be able to parse object type spread.
  // Flow can still parse it using the comment syntax (/* :: bla */) though.
  /* :: ...MemoryHistoryWithoutPushAndReplace, */

  // These are compatible with the push/replace from MemoryHistory but
  // restricted so that you have to use the string form (instead of
  // { pathname, state }), so that we can typecheck the route you're navigating
  // to (using RouteToLocationStateMappings).
  /* eslint-disable no-undef */
  push: <RouteType: Route>(RouteType, LocationStateForRoute<RouteType>) => void,
  replace: <RouteType: Route>(
    RouteType,
    LocationStateForRoute<RouteType>
  ) => void,
  /* eslint-enable no-undef */

  // The location that just got replaced. We need to keep it around so that we
  // can animate it off the page.
  replacedLocation: ?{
    location: MemoryLocation,
    previousIndex: number,
  },

  // When doing a clearHistoryAndPush, this is true.
  _isDoingClearHistoryAndPush: boolean,

  // When doing a clearHistoryAndVirtualPopTo, this is true.
  _isDoingVirtualPop: boolean,

  computeEffectiveAction: () =>
    | Action
    | "INITIAL_REPLACE"
    | "CLEAR_HISTORY_AND_PUSH"
    | "VIRTUAL_POP"
    | "IGNORE",
|};

// This has to be a named export because our babel setup doesn't handle
// mutable default exports properly.
// This is technically a ?NexiaHistory, but once React is mounted, it's a
// NexiaHistory, and React is mounted 99.9% of the time the app is running, so
// treat it like a NexiaHistory instead of making other people treat it as
// a maybe for no real benefit.
// $FlowIgnore
export let history: NexiaHistory = null; // eslint-disable-line import/no-mutable-exports

function patchHistory(hist) {
  const originalHistoryReplace = hist.replace;
  hist.replace = <RouteType: Route>(
    path: RouteType,
    state: LocationStateForRoute<RouteType>
  ) => {
    hist.replacedLocation = {
      location: hist.entries[hist.index],
      previousIndex: hist.index,
    };
    return originalHistoryReplace.call(hist, path, state);
  };

  const originalHistoryPush = hist.push;
  hist.push = <RouteType: Route>(
    path: RouteType,
    state: LocationStateForRoute<RouteType>
  ) => {
    hist.replacedLocation = null;
    return originalHistoryPush.call(hist, path, state);
  };

  const originalGoBack = hist.goBack;
  hist.goBack = () => {
    hist.replacedLocation = null;
    return originalGoBack.call(hist);
  };

  const originalGoForward = hist.goForward;
  hist.goForward = () => {
    hist.replacedLocation = null;
    return originalGoForward.call(hist);
  };

  const originalGo = hist.go;
  hist.go = (amount: number) => {
    hist.replacedLocation = null;
    return originalGo.call(hist, amount);
  };

  hist.computeEffectiveAction = () => {
    // React router only has three action types: PUSH, POP, and REPLACE.
    // We can handle transitions for those all pretty well.
    // However, there are three places in our app that need to be
    // transitioned differently: the initial REPLACE that occurs when we
    // navigate to /app, the POP and REPLACE that occur when we use
    // clearHistoryAndPush, and the POP and REPLACE that occur when we use
    // clearHistoryAndVirtualPopTo. To differentiate those three, we're going to
    // fake INITIAL_REPLACE, CLEAR_HISTORY_AND_PUSH, and VIRTUAL_POP action
    // types.
    let action = hist.action;
    if (hist._isDoingClearHistoryAndPush) {
      if (action === "POP") {
        // A clearHistoryAndPush consists of two parts: a POP and a REPLACE.
        // We want to treat it as one atomic CLEAR_HISTORY_AND_PUSH, so
        // ignore the POP phase.
        action = "IGNORE";
      } else if (hist._isDoingVirtualPop) {
        action = "VIRTUAL_POP";
      } else {
        action = "CLEAR_HISTORY_AND_PUSH";
      }
    }

    // We don't want to transition the first REPLACE from /app to /app/home in,
    // we just want to teleport /app/home in. So treat it special.
    if (
      hist.action === "REPLACE" &&
      hist.replacedLocation &&
      (hist.replacedLocation.location.pathname === "/app" ||
        (hist.replacedLocation.location.pathname != null &&
          !hist.replacedLocation.location.pathname.startsWith("/app"))) // pin entry screen, etc
    ) {
      action = "INITIAL_REPLACE";
    }

    return action;
  };
}

export function setHistory(newHistory: NexiaHistory) {
  if (history === newHistory) {
    return;
  }

  patchHistory(newHistory);
  history = newHistory;
}

export function clearHistoryAndPush<RouteType: Route>(
  pathname: RouteType,
  locationState: LocationStateForRoute<RouteType>
) {
  if (history == null) {
    throw new Error("Attempted to use history before it was ready");
  }
  history._isDoingClearHistoryAndPush = true;
  TrackedScrollableArea.forgetAll();
  batchReactUpdates(() => {
    history.go(1 - history.length);
    history.replace(pathname, locationState);
  });

  // Don't clear the _isDoingClearHistoryAndPush until a tick from now so that
  // componentDidMount in HistoryPageTransitionController can use
  // computeEffectiveAction in componentDidUpdate to check if this was a
  // CLEAR_HISTORY_AND_PUSH.
  enqueueIn(() => {
    history._isDoingClearHistoryAndPush = false;
  }, 0);
}

export function clearHistoryAndVirtualPopTo<RouteType: Route>(
  pathname: RouteType,
  locationState: LocationStateForRoute<RouteType>
) {
  if (history == null) {
    throw new Error("Attempted to use history before it was ready");
  }

  history._isDoingVirtualPop = true;
  clearHistoryAndPush(pathname, locationState);

  // Don't clear the _isDoingVirtualPop until a tick from now so that
  // componentDidMount in HistoryPageTransitionController can use
  // computeEffectiveAction in componentDidUpdate to check if this was a
  // VIRTUAL_POP.
  enqueueIn(() => {
    history._isDoingVirtualPop = false;
  }, 0);
}

export function goBackTo(pathname: Route) {
  const index = history.entries.findIndex(
    (entry) => entry.pathname === pathname
  );
  if (index === -1) {
    throw new Error(
      `Attempted to go back to ${pathname}, but it wasn't in history.\nThe following entries were in history:\n${history.entries
        .map((entry) => `  ${entry.pathname || "(no pathname)"}`)
        .join("\n")}`
    );
  }
  history.go(1 + index - history.length);
}



// WEBPACK FOOTER //
// ./src/history.js